import { fetch } from '@/services/fetch'
import { BASE_CURRENCY } from '@/enums/Currency'
import { getParams } from '@/services/params'
import { CcnPaymentMethod } from '@/enums/CcnPaymentMethod'

export type CreditNoteListResponse = {
  '@context': string
  '@id': string
  '@type': 'hydra:Collection'
  'hydra:member': CcnListItem[]
  'hydra:totalItems': number
  'hydra:view': any
}

const buildUrl = (base: string, params?: any[]): string => {
  if (!params) return base
  const searchParams = getParams(params)
  return searchParams.size ? `${base}?${searchParams.toString()}` : base
}

const getCreditNotes = async (
  params?: any[],
  linguistId?: string
): Promise<CreditNoteListResponse> => {
  const base = linguistId ? `/linguists/${linguistId}/ccns` : '/ccns'
  return await fetch(buildUrl(base, params))
}

const getCreditNote = async (id: string, params?: any[]): Promise<CcnEntity> => {
  return await fetch(buildUrl(`/ccns/${id}`, params))
}

const cancelCreditNote = async (id: string, cancellationDate: Date): Promise<CcnEntity> => {
  // Send the local calendar date the admin picked, not the UTC ISO equivalent —
  // toISOString() shifts the day backwards for any user east of UTC (e.g. a
  // Nov 4 pick in CET becomes "2025-11-03T23:00:00.000Z").
  const year = cancellationDate.getFullYear()
  const month = String(cancellationDate.getMonth() + 1).padStart(2, '0')
  const day = String(cancellationDate.getDate()).padStart(2, '0')
  return await fetch(`/ccns/${id}/cancel`, {
    method: 'put',
    data: { cancellationDate: `${year}-${month}-${day}` }
  })
}

const syncCreditNotePaymentStatus = async (id: string): Promise<CcnEntity> => {
  // Endpoint has `deserialize: false` on the API side — body is ignored;
  // the processor queries Buchhaltungsbutler for the current status.
  return await fetch(`/ccns/${id}/update_accounting_payment_status`, {
    method: 'post',
    data: {}
  })
}

const paySepa = async (ids: string[]): Promise<unknown> => {
  return await fetch('/ccns/pay_sepa', {
    method: 'put',
    data: { ids: ids.join(','), paymentMethod: CcnPaymentMethod.BANK_TRANSFER }
  })
}

const exportSepa = async (ids: string[]): Promise<Blob> => {
  return await fetch(
    `/ccns/export_sepa/${encodeURIComponent(ids.join(','))}/${encodeURIComponent(CcnPaymentMethod.BANK_TRANSFER)}`,
    { method: 'GET', responseType: 'blob' }
  )
}

const formatDatevDate = (date: Date): string => {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}.${mm}.${dd}`
}

const exportDatev = async (from: Date, to: Date): Promise<{ blob: Blob; filename: string }> => {
  const fromStr = formatDatevDate(from)
  const toStr = formatDatevDate(to)
  // CCN's Datev controller signature is just (from, to, user); the route's
  // optional {collective?} segment is ignored for CCNs.
  const blob = await fetch(`/ccns/export_datev/${fromStr}/${toStr}`, {
    method: 'GET',
    responseType: 'blob'
  })
  return { blob, filename: `Ccns_Datev_${fromStr}_${toStr}.csv` }
}

// CCN editor (#1829) — composer endpoints.
//
// The editor is reachable only from a cancelled CCN. `previewDraftCcn` returns
// a watermarked PDF without any DB writes; `createCcnFromCancelled` atomically
// persists a fresh CCN and (optionally) emails the linguist. Both endpoints
// share the same draft payload shape.
const previewDraftCcn = async (cancelledCcnId: string, payload: CcnDraftPayload): Promise<Blob> => {
  return await fetch(`/ccns/${cancelledCcnId}/draft_pdf`, {
    method: 'post',
    data: payload,
    responseType: 'blob'
  })
}

const createCcnFromCancelled = async (
  cancelledCcnId: string,
  payload: CcnDraftPayload
): Promise<{
  '@id': string
  id: string
  receiptNumber: string
  sent: boolean
  status: string
  payoutCurrency?: string
  exchangeRate?: string | null
  payoutFinalFee?: string | null
}> => {
  return await fetch(`/ccns/${cancelledCcnId}/create_from_cancelled`, {
    method: 'post',
    data: payload
  })
}

const sendCreditNoteToLinguist = async (
  id: string
): Promise<{ id: string; receiptNumber: string; sent: boolean; sentDate: string | null }> => {
  // POST /ccns/{id}/send re-runs the CCN-create email path: regenerate the PDF
  // from current CCN data, dispatch the LSP email, flip sent + sentDate +
  // workflow SENT transition. Idempotent — re-clickable for a real re-send.
  return await fetch(`/ccns/${id}/send`, {
    method: 'post',
    data: {}
  })
}

const getCreditNoteAuditLog = async (id: string): Promise<CcnAuditLogEntry[]> => {
  // The audit-log endpoint mirrors `/jobs/{id}/fee-history` — Gedmo's
  // LogEntryRepository serialises directly, so the response is a plain array.
  const response = await fetch(`/ccns/${id}/audit_log`)
  return Array.isArray(response) ? response : []
}

// Mirrors VATHelper::TAX_RATE on the api side. Hard-coded for now; the api
// returns no derived rate, only the raw eligibility primitives. If/when the
// backend exposes the rate explicitly, replace this constant with the
// server-provided value.
const STANDARD_GERMAN_VAT_RATE = 0.19

const getCcnLinguistDefaults = async (ccnId: string): Promise<CcnLinguistDefaults> => {
  // The linguist's "billing address" for a CCN is a composite of three
  // sources — the same composition the CCN PDF service uses in production:
  //   - firstName / lastName  → User.firstName / User.lastName (NOT the
  //     BillingAddress entity's own first/lastName which are typically null
  //     for linguist accounts)
  //   - vatId                 → PaymentData.vatId
  //   - everything else       → BillingAddress fields
  //
  // VAT eligibility comes from the same payload — JobService::updateJobFinalFee
  // applies VAT when `paymentData.smallCompany === false` AND
  // `billingAddress.addressCountry === 'DE'`. We compute that here so the
  // composer doesn't need to encode the rule.
  //
  // MaxDepth(1) on Ccn.linguist hides these from the main CCN response, so we
  // hit the dedicated subresource with address:read + payment-data:read so
  // BillingAddress and PaymentData are serialised at their fields rather
  // than as JSON-LD references.
  const params = new URLSearchParams()
  params.append('groups[]', 'address:read')
  params.append('groups[]', 'payment-data:read')
  const linguist = (await fetch(`/ccns/${ccnId}/linguist?${params.toString()}`)) as {
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    inhouseTranslators?: boolean | null
    billingAddress?: Partial<CcnBillingAddressSnapshot> | null
    paymentData?: {
      vatId?: string | null
      taxNumber?: string | null
      smallCompany?: boolean | null
    } | null
    payoutCurrency?: string | null
    payoutExchangeRate?: string | null
    fixedFeePerOrder?: string | null
  }
  const address = linguist.billingAddress ?? {}
  const billingAddress: CcnBillingAddressSnapshot = {
    firstName: linguist.firstName ?? address.firstName ?? null,
    lastName: linguist.lastName ?? address.lastName ?? null,
    companyName: address.companyName ?? null,
    vatId: linguist.paymentData?.vatId ?? address.vatId ?? null,
    streetAddress: address.streetAddress ?? null,
    houseNumber: address.houseNumber ?? null,
    addressSuffix: address.addressSuffix ?? null,
    postalCode: address.postalCode ?? null,
    addressLocality: address.addressLocality ?? null,
    addressCountry: address.addressCountry ?? null,
    addressRegion: address.addressRegion ?? null,
    postOfficeBoxNumber: address.postOfficeBoxNumber ?? null
  }
  const paymentData: CcnPaymentDataSnapshot = {
    vatId: linguist.paymentData?.vatId ?? null,
    taxNumber: linguist.paymentData?.taxNumber ?? null,
    smallCompany: linguist.paymentData?.smallCompany ?? false
  }
  const vatEligible = paymentData.smallCompany === false && address.addressCountry === 'DE'
  return {
    email: linguist.email ?? null,
    firstName: linguist.firstName ?? null,
    lastName: linguist.lastName ?? null,
    inhouseTranslators: linguist.inhouseTranslators ?? false,
    billingAddress,
    paymentData,
    vatEligible,
    vatRate: STANDARD_GERMAN_VAT_RATE,
    payoutCurrency: linguist.payoutCurrency ?? BASE_CURRENCY,
    payoutExchangeRate: linguist.payoutExchangeRate ?? null,
    fixedFeePerOrder: linguist.fixedFeePerOrder ?? null
  }
}

const getEligibleJobsForCcnLinguist = async (
  linguistId: string,
  currentCcnSource?: string
): Promise<unknown[]> => {
  // Returns a hydra collection; the composer just needs the member list.
  // Cap at 50 — the combobox is a manual picker, not a paginated list, and
  // on production-sized dumps the default page-size + `job:list` group
  // produces a heavy serialisation that can take several seconds.
  //
  // `currentCcnSource` opts the request into the cross-cancelled-CCN
  // re-composition flow: the api extension then surfaces jobs currently
  // linked to OTHER cancelled CCNs of the same linguist alongside the
  // truly-orphan ones. The extra job:ccn-source + ccn:list + cancelable:read
  // groups carry the nested ccn's receipt-number + cancelled flag so the
  // dropdown can annotate cross-source entries (e.g. "from CCN-DEMO-015-c").
  const params = new URLSearchParams({
    eligibleForCcnLinguist: linguistId,
    itemsPerPage: '50'
  })
  params.append('groups[]', 'job:list')
  params.append('groups[]', 'job:ccn-composer')
  if (currentCcnSource) {
    params.append('currentCcnSource', currentCcnSource)
    params.append('groups[]', 'job:ccn-source')
    params.append('groups[]', 'ccn:list')
    params.append('groups[]', 'cancelable:read')
  }
  const response = await fetch(`/jobs?${params.toString()}`)
  return (response as { 'hydra:member'?: unknown[] })['hydra:member'] ?? []
}

export {
  getCreditNotes,
  getCreditNote,
  cancelCreditNote,
  syncCreditNotePaymentStatus,
  paySepa,
  exportSepa,
  exportDatev,
  previewDraftCcn,
  createCcnFromCancelled,
  sendCreditNoteToLinguist,
  getCreditNoteAuditLog,
  getCcnLinguistDefaults,
  getEligibleJobsForCcnLinguist
}
